import { FC, useCallback, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
// Redux
import { getLiveScreen, resetLiveScreen, updateLiveScreen } from 'redux/dashboard-service/action';
import { selectorGetLiveScreen } from 'redux/dashboard-service/selector';
import { SET_ERROR } from 'redux/actions-types';
import { ILiveScreenInfo } from 'redux/dashboard-service/types';
// Formik
import { Formik } from 'formik';
// Components
import { LiveScreenForm } from './LiveScreenForm';
// Api
import { API } from 'core/API';
// Styles
import CongratsPopup from 'components/Organization/CongratsPopup';
import useThunkDispatch from '../../../core/customHooks/useThunkDispatch';
import { DashboardSettingsUpdateRequest } from '@joc/api-gateway/lib/api-client';

const validationScheme = Yup.object().shape({
	title: Yup.string().required(),
	subTitle: Yup.string().required(),
	interval: Yup.number().min(1, 'Interval must be at least 1').required('Interval is required'),
	mediaPathes: Yup.array().min(1, 'Media is required').required('Media is required'),
});

type LiveScreenProps = {
	// eslint-disable-next-line no-unused-vars
	onChangeEditMode: (value: boolean) => void;
	editMode: boolean;
};

export const LiveScreen: FC<LiveScreenProps> = ({ onChangeEditMode, editMode }) => {
	const dispatch = useThunkDispatch();

	const liveScreenInfo = useSelector(selectorGetLiveScreen);
	const [isCongrats, setIsCongrats] = useState(false);

	type FormValues = Omit<ILiveScreenInfo, 'mediaPathes'> & {
		mediaPathes: (string | File)[];
	};

	const [formValue, setFormValue] = useState<Partial<FormValues>>({
		title: '',
		subTitle: '',
		mediaPathes: [],
		interval: 10,
	});

	useEffect(() => {
		dispatch(getLiveScreen());

		return () => {
			dispatch(resetLiveScreen());
		};
	}, []);

	useEffect(() => {
		if (liveScreenInfo) initFormData();
	}, [liveScreenInfo]);

	const initFormData = () => {
		if (liveScreenInfo) {
			const data = {
				title: liveScreenInfo.title,
				subTitle: liveScreenInfo.subTitle,
				mediaPath: liveScreenInfo.mediaPathes,
				mediaPathes: (liveScreenInfo.mediaPathes || []) as (string | File)[],
				interval: liveScreenInfo.interval || 10,
			};
			setFormValue(data);
		} else {
			setFormValue({
				title: '',
				subTitle: '',
				mediaPathes: [],
				interval: 10,
			});
		}
	};

	const onCancelEditMode = () => {
		initFormData();
		onChangeEditMode(false);
	};

	const handleSubmit = useCallback(
		async (values: FormValues) => {
			try {
				const body: Partial<ILiveScreenInfo> = {
					title: values.title,
					subTitle: values.subTitle,
					interval: values.interval,
				};
				const mediaPaths: string[] = [];

				// Process all media items: upload new files and collect existing URLs
				const uploadPromises = (values.mediaPathes || []).map(async (item: string | File) => {
					if (item instanceof File) {
						// Upload new file
						const fileName = item.name;
						const responseUpload = await API.uploadFile({ data: item, fileName });
						return `${process.env.REACT_APP_S3_API}/${responseUpload.fileName}`;
					}
					// Keep existing URL
					return item as string;
				});

				const results = await Promise.all(uploadPromises);
				mediaPaths.push(...results);

				if (mediaPaths.length > 0) {
					body.mediaPathes = mediaPaths;
				}

				body.title = body.title ? body.title.trim() : '';
				body.subTitle = body.subTitle ? body.subTitle.trim() : '';
				body.interval = values.interval || 10;

				await dispatch(updateLiveScreen(DashboardSettingsUpdateRequest.fromJS(body)));
				onChangeEditMode(false);
				setIsCongrats(true);
				await dispatch(getLiveScreen());
			} catch (error: any) {
				dispatch({ type: SET_ERROR, payload: { state: true, message: error.message } });
			}
		},
		[dispatch]
	);

	return (
		<>
			<Formik
				enableReinitialize={true}
				validationSchema={validationScheme}
				initialValues={formValue}
				onSubmit={() => {}}
			>
				<LiveScreenForm editMode={editMode} onCancelEditMode={onCancelEditMode} onSaveChanges={handleSubmit} />
			</Formik>
			{isCongrats && (
				<CongratsPopup
					title="Success!"
					subtitle="Information has been updated."
					doneButtonClickHandler={() => setIsCongrats(false)}
					isFullHeight={true}
					isLiveScreen={true}
				/>
			)}
		</>
	);
};
